import {
  AwaitContainer,
  Button,
  Checkbox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Switch,
} from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserResponse, useUsers } from '@/entities/users';
import { IsActiveBadge } from '@/features/IsActiveBadge';
import { NestedItemCard } from '@/features/nested-items';
import { getFullName, getShortName } from '@/shared/lib';

type Props = {
  existingUserIds: number[]; // Идентификаторы уже выбранных пользователей
  onCancel: () => void;
  onSave: (users: UserResponse[]) => void;
  className?: string;
};

export const AddUsersDialog: FC<Props> = ({ existingUserIds, onCancel, onSave, className }) => {
  const { t } = useTranslation(['common', 'users']);

  // Опционально: фильтрация по статусу (например, isActive)
  // По умолчанию показываем только активных пользователей
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  // Отдельный запрос для диалога (может иметь дополнительные параметры)
  // React Query автоматически использует кэш, если параметры совпадают
  const { data, isLoading } = useUsers({ limit: 100, isActive: showOnlyActive || undefined });

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  // Исключаем уже выбранных пользователей
  const availableUsers: UserResponse[] = useMemo(() => {
    return data?.users?.filter((user) => !existingUserIds.includes(user.id)) || [];
  }, [existingUserIds, data?.users]);

  // Локальная фильтрация по поисковому запросу
  const filtered = useMemo(() => {
    if (!search.trim()) return availableUsers;
    const q = search.toLowerCase();
    return availableUsers.filter((user) => {
      const fullName = getFullName(user.firstName, user.lastName, user.middleName).toLowerCase();
      const role = user.role?.name?.toLowerCase() || '';
      return fullName.includes(q) || role.includes(q) || user.email.toLowerCase().includes(q);
    });
  }, [availableUsers, search]);

  const handleSave = () => {
    // Передаем только выбранные пользователи
    onSave(availableUsers.filter((user) => selectedIds.includes(user.id)));
  };

  const renderUserTitle = (user: UserResponse) => {
    return (
      <span>
        {getShortName(user.firstName, user.lastName, user.middleName)}
        {user.role?.name && <span className="text-sm text-muted-foreground ml-2">{user.role.name}</span>}
        <IsActiveBadge isActive={user.isActive} className="ml-3" />
      </span>
    );
  };

  return (
    <DialogContent className={className}>
      <DialogHeader>
        <DialogTitle>{t('users:addUser')}</DialogTitle>
      </DialogHeader>
      <div className="flex min-w-0 flex-col gap-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common:search')} />

        {/* Опционально: переключатель фильтрации */}
        <Switch value={showOnlyActive} onChange={setShowOnlyActive} label={t('common:showActive')} />

        <AwaitContainer isLoading={isLoading}>
          <div className="flex flex-col gap-3 max-h-120 overflow-auto">
            {filtered.map((user) => (
              <NestedItemCard
                key={user.id}
                item={user}
                renderTitle={renderUserTitle}
                renderDescription={(item) => item.email}
                action={<Checkbox value={selectedIds.includes(user.id)} onChange={() => handleToggle(user.id)} />}
              />
            ))}
            {filtered.length === 0 && <div className="text-muted-foreground">{t('users:notFound')}</div>}
          </div>
        </AwaitContainer>
      </div>

      <DialogFooter>
        <div className="flex gap-3 justify-end w-full">
          <Button variant="ghost" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSave} disabled={selectedIds.length === 0}>
            {t('common:add')}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};
